import { TranslationOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Button, Form, Input, InputNumber, Modal, Select, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  SalonService,
  SalonServiceUpdatePayload,
  SalonStylistOption,
} from "../../@type/salonService.types";
import { SalonAPI } from "../../api/Salon";

export interface ServiceCategoryOption {
  id: number;
  name: string;
}

interface ServiceEditModalProps {
  open: boolean;
  outletId: string;
  service: SalonService | null;
  categoryOptions: ServiceCategoryOption[];
  onCancel: () => void;
  onSuccess?: (updatedService: SalonService, nameChanged: boolean) => void;
  onManageTranslations?: () => void;
}

const formatNumber = (value: string | number | undefined) => {
  if (value === undefined || value === null || value === "") return "";
  const [integerPart, ...decimalParts] = `${value}`.split(".");
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalParts.length ? `${grouped}.${decimalParts.join("")}` : grouped;
};

// antd InputNumber types `parser` as returning a number, but a numeric string is what it expects here.
const parseNumber = (value: string | undefined) =>
  (value || "").replace(/[^\d.]/g, "") as unknown as number;

const blockNonNumericKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.ctrlKey || event.metaKey || event.key.length > 1) return;
  if (!/[\d.]/.test(event.key)) event.preventDefault();
};

interface ServiceEditFormValues {
  name: string;
  category_id: number | null;
  price: number | null;
  discount_price: number | null;
  duration: number | null;
  stylist_ids: number[];
}

const getErrorMessage = (error: unknown, fallback: string, forbidden: string) => {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })
    ?.response;

  if (response?.status === 403) {
    return forbidden;
  }

  if (response?.data?.message) return response.data.message;
  if (error instanceof Error) return error.message;
  return fallback;
};

const ServiceEditModal: React.FC<ServiceEditModalProps> = ({
  open,
  outletId,
  service,
  categoryOptions,
  onCancel,
  onSuccess,
  onManageTranslations,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ServiceEditFormValues>();
  const [saving, setSaving] = useState(false);
  const [stylists, setStylists] = useState<SalonStylistOption[]>([]);

  const allCategoryOptions = useMemo(() => {
    const options = [...categoryOptions];
    if (
      service?.category_id &&
      !options.some((option) => option.id === service.category_id)
    ) {
      options.push({
        id: service.category_id,
        name: service.category_name || t("salon.service_edit.category_fallback", { id: service.category_id }),
      });
    }

    return options;
  }, [categoryOptions, service, t]);

  useEffect(() => {
    if (!open || !outletId) return;

    SalonAPI.getSalonStylists(outletId)
      .then((response) => setStylists(response.data.data || []))
      .catch((error) => {
        console.error("Error fetching salon stylists:", error);
        toast.error(t("salon.service_edit.messages.stylists_failed"));
      });
  }, [open, outletId, t]);

  useEffect(() => {
    if (!open || !service) return;

    form.setFieldsValue({
      name: service.name || "",
      category_id: service.category_id,
      price: service.price,
      discount_price: service.discount_price,
      duration: service.duration,
      stylist_ids: service.stylist_ids || [],
    });
  }, [form, open, service]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    if (!outletId || !service) return;

    try {
      const values = await form.validateFields();
      const payload: SalonServiceUpdatePayload = {
        name: values.name.trim(),
        price: values.price ?? null,
        discount_price: values.discount_price ?? null,
        duration: values.duration ?? null,
        category_id: values.category_id ?? null,
        stylist_ids: values.stylist_ids || [],
      };

      setSaving(true);
      const response = await SalonAPI.updateSalonService(outletId, service.id, payload);
      const nameChanged = payload.name !== (service.name || "").trim();
      toast.success(t("salon.service_edit.messages.updated"));
      handleCancel();
      onSuccess?.(response.data.data, nameChanged);
    } catch (error) {
      const isValidationError =
        typeof error === "object" && error !== null && "errorFields" in error;

      if (!isValidationError) {
        console.error("Error updating salon service:", error);
        toast.error(
          getErrorMessage(
            error,
            t("salon.service_edit.messages.update_failed"),
            t("salon.service_edit.messages.forbidden")
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      width={560}
      title={null}
      closable={false}
      styles={{ content: { borderRadius: "12px", overflow: "hidden", padding: 0 } }}
      footer={
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          px={3}
          py={2}
          borderTop="1px solid var(--app-border-light)"
        >
          <Button style={{ width: "120px" }} onClick={handleCancel} disabled={saving}>
            {t("btn.cancel")}
          </Button>
          <Button
            type="primary"
            style={{ width: "120px", background: "#ba2156", border: "none" }}
            onClick={handleSave}
            loading={saving}
            disabled={!service || saving}
          >
            {t("btn.save")}
          </Button>
        </Box>
      }
    >
      <Box bgcolor="#ba2156" color="#fff" px={3} py={2}>
        <Box fontSize="18px" fontWeight={700} lineHeight={1.3}>
          {t("salon.service_edit.title")}
        </Box>
        <Box fontSize="12px" sx={{ opacity: 0.85 }}>
          {service?.name || t("salon.service_edit.fallback_subtitle")}
        </Box>
      </Box>
      <Box px={3} py={3}>
      <Form form={form} layout="vertical" className="service-edit-form">
        <div
          style={{
            marginBottom: 16,
            padding: "16px",
            background: "var(--app-bg)",
            borderRadius: "6px",
            border: "1px solid var(--app-border-light)",
          }}
        >
          <Form.Item
            label={t("salon.service_edit.fields.name")}
            name="name"
            rules={[
              { required: true, message: t("salon.service_edit.validation.name") },
              {
                validator: (_, value) =>
                  String(value || "").trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("salon.service_edit.validation.name"))),
              },
            ]}
          >
            <Input
              placeholder={t("salon.service_edit.placeholders.name")}
              suffix={
                <Tooltip title={t("salon.service_edit.manage_translations")}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t("salon.service_edit.manage_translations")}
                    icon={<TranslationOutlined style={{ color: "#ba2156" }} />}
                    disabled={!service}
                    onClick={() => onManageTranslations?.()}
                  />
                </Tooltip>
              }
            />
          </Form.Item>

          <Form.Item
            label={t("salon.service_edit.fields.category")}
            name="category_id"
            rules={
              allCategoryOptions.length > 0
                ? [{ required: true, message: t("salon.service_edit.validation.category") }]
                : []
            }
          >
            <Select
              placeholder={t("salon.service_edit.placeholders.category")}
              disabled={allCategoryOptions.length === 0}
              options={allCategoryOptions.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label={t("salon.service_edit.fields.price")}
            name="price"
            rules={[{ required: true, message: t("salon.service_edit.validation.price") }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder={t("salon.service_edit.placeholders.price")}
            />
          </Form.Item>

          <Form.Item
            label={t("salon.service_edit.fields.discount_price")}
            name="discount_price"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const price = getFieldValue("price");
                  if (value === null || value === undefined || value === "") {
                    return Promise.resolve();
                  }
                  if (price === null || price === undefined || price === "") {
                    return Promise.resolve();
                  }
                  return Number(value) <= Number(price)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t("salon.service_edit.validation.discount"))
                      );
                },
              }),
            ]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder={t("salon.service_edit.placeholders.discount_price")}
            />
          </Form.Item>

          <Form.Item
            label={t("salon.service_edit.fields.duration")}
            name="duration"
            rules={[{ required: true, message: t("salon.service_edit.validation.duration") }]}
          >
            <InputNumber
              min={0}
              precision={0}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder={t("salon.service_edit.placeholders.duration")}
            />
          </Form.Item>

          <Form.Item label={t("salon.service_edit.fields.stylists")} name="stylist_ids">
            <Select
              mode="multiple"
              allowClear
              placeholder={t("salon.service_edit.placeholders.stylists")}
              optionFilterProp="label"
              disabled={stylists.length === 0}
              options={stylists.map((stylist) => ({
                value: stylist.id,
                label: stylist.name,
              }))}
            />
          </Form.Item>
        </div>
      </Form>
      </Box>
    </Modal>
  );
};

export default ServiceEditModal;
